import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Group as PanelGroup, Panel } from 'react-resizable-panels';
import { useAuth } from '../../components/auth/AuthContext';
import ClientTradesGrid from '../../components/grids/ClientTradesGrid';
import { WorkflowState } from '../../types/workflow';
import EmailPreviewPanel from '../../components/confirmations/EmailPreviewPanel';
import PdfPreviewPanel from '../../components/confirmations/PdfPreviewPanel';
import ComparisonTable from '../../components/confirmations/ComparisonTable';
import ExceptionsModal from '../../components/confirmations/ExceptionsModal';
import Toast from '../../components/common/Toast';
import ResizeHandle from '../../components/common/ResizeHandle';
import { clientService } from '../../services/clientService';
import { ConfirmationData, ComparisonField } from '../../types/confirmation';
import { buildComparisonFields } from '../../utils/confirmationComparison';
import { useEventStreamSimple } from '../../hooks/useEventStreamSimple';
import { logger } from '../../utils/logger';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useSystemDate } from '../../hooks/useSystemDate';
import * as ExcelJS from 'exceljs';
import './Confirmations.css';

const Confirmations: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.substring(0, 2) || 'es';
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;

  // System date + date range filter (last 3 business days by default: T-2 → T0)
  const systemDateResult = useSystemDate(clientId);
  const { systemDate, previousBusinessDay2 } = systemDateResult;
  const [dateFrom, setDateFrom] = useState<string | null>(previousBusinessDay2 || null);
  const [dateTo, setDateTo] = useState<string | null>(systemDate || null);
  const dateDefaultsSet = useRef(false);

  useEffect(() => {
    if (systemDate && previousBusinessDay2 && !dateDefaultsSet.current) {
      dateDefaultsSet.current = true;
      setDateFrom(previousBusinessDay2);
      setDateTo(systemDate);
    }
  }, [systemDate, previousBusinessDay2]);

  // State
  const [selectedTrade, setSelectedTrade] = useState<Record<string, unknown> | null>(null);
  const [confirmationData, setConfirmationData] = useState<ConfirmationData | null>(null);
  const [activeTab, setActiveTab] = useState<'email' | 'pdf'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Toast state for real-time notifications
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Track shown event IDs to prevent duplicate toasts (survives page refresh via sessionStorage)
  const SEEN_EVENTS_KEY = 'confirmations_seen_event_ids';
  const seenEventIds = useRef<Set<string>>(new Set<string>());
  if (seenEventIds.current.size === 0) {
    try {
      const stored = sessionStorage.getItem(SEEN_EVENTS_KEY);
      if (stored) {
        const ids: string[] = JSON.parse(stored);
        ids.forEach(id => seenEventIds.current.add(id));
      }
    } catch { /* ignore corrupt storage */ }
  }

  // Exceptions modal state
  const [showExceptionsModal, setShowExceptionsModal] = useState(false);
  const [exceptionsCount, setExceptionsCount] = useState(0);

  // Export trigger for grid
  const [exportTrigger, setExportTrigger] = useState(0);
  // Clear filters trigger for grid
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);

  // Field selection state for highlighting
  const [selectedField, setSelectedField] = useState<string | null>(null);

  // Build comparison fields for determining comparison result of selected field
  const comparisonFields: ComparisonField[] = useMemo(() => {
    if (!selectedTrade || !confirmationData) return [];
    return buildComparisonFields(selectedTrade, confirmationData, locale);
  }, [selectedTrade, confirmationData, locale]);

  // Get the raw text for email highlighting (if rawTextValues are available)
  const highlightText = useMemo(() => {
    if (!selectedField || !confirmationData?.rawTextValues) return undefined;
    return confirmationData.rawTextValues[selectedField];
  }, [selectedField, confirmationData?.rawTextValues]);

  // Get occurrence index for email highlighting (KAN-147: label-proximity)
  const highlightOccurrenceIndex = useMemo(() => {
    if (!selectedField || !confirmationData?.rawTextOccurrenceIndices) return 0;
    return confirmationData.rawTextOccurrenceIndices[selectedField] ?? 0;
  }, [selectedField, confirmationData?.rawTextOccurrenceIndices]);

  // Get comparison result for the selected field (for color coding highlights)
  const selectedFieldResult = useMemo(() => {
    if (!selectedField) return undefined;
    const field = comparisonFields.find(f => f.fieldName === selectedField);
    return field?.comparisonResult;
  }, [selectedField, comparisonFields]);

  // Clear selected field when trade changes
  useEffect(() => {
    setSelectedField(null);
  }, [selectedTrade]);

  // Auto-switch to PDF tab when selecting a field that has PDF coordinates
  useEffect(() => {
    if (selectedField && confirmationData?.fieldCoordinates?.[selectedField]) {
      setActiveTab('pdf');
    }
  }, [selectedField, confirmationData?.fieldCoordinates]);

  // Function to trigger refresh of grid
  const triggerGridRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Helper function to show toast notifications
  const showToastNotification = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  }, []);

  // Export grid to Excel (receives columns + sorted/filtered cell values from grid)
  const handleExportData = useCallback(async (
    columns: { header: string; field: string }[],
    rows: any[][]
  ) => {
    if (rows.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Confirmations');

    // Header row from grid's displayed columns
    const headerRow = worksheet.addRow(columns.map(c => c.header));
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2D2D2D' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });

    // Data rows (already resolved by grid's valueGetters)
    for (const row of rows) {
      worksheet.addRow(row);
    }

    // Auto-fit column widths
    worksheet.columns.forEach((col, i) => {
      const header = columns[i]?.header || '';
      col.width = Math.max(header.length + 2, 12);
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `confirmations_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // Connect to real-time event stream
  useEventStreamSimple({
    clientId: clientId,
    onEvent: (eventData) => {
      logger.log('🎉 [Confirmations] Received event:', eventData.type, eventData.title);

      // Deduplicate: skip events we've already shown a toast for
      const eventId = eventData.id;
      if (eventId && seenEventIds.current.has(eventId)) {
        logger.log('⏭️ [Confirmations] Skipping already-shown event:', eventId);
      } else {
        // Check if event is recent (within last 2 minutes)
        const eventTime = new Date(eventData.timestamp);
        const now = new Date();
        const ageInMinutes = (now.getTime() - eventTime.getTime()) / (1000 * 60);

        // Only show toast for recent events
        if (ageInMinutes < 2) {
          // Determine toast type based on event content
          let toastTypeForEvent: 'success' | 'error' | 'info' | 'warning' = 'success';
          if (eventData.type === 'duplicate_detected') {
            toastTypeForEvent = 'warning';
          } else if (eventData.payload?.error) {
            toastTypeForEvent = 'error';
          }
          const titleKey = eventData.payload?.title_key;
          const toastTitle = titleKey
            ? t(`confirmations.${titleKey}`, eventData.title)
            : eventData.title;
          showToastNotification(toastTitle, toastTypeForEvent);
        }

        // Mark as seen (regardless of age — we don't want old events popping up later)
        if (eventId) {
          seenEventIds.current.add(eventId);
          try {
            sessionStorage.setItem(SEEN_EVENTS_KEY, JSON.stringify([...seenEventIds.current]));
          } catch { /* storage full — non-critical */ }
        }
      }

      // Refresh grid for relevant events (always, even if toast was deduplicated)
      if (eventData.type === 'gmail_processed' ||
          eventData.type === 'trade_matched' ||
          eventData.type === 'match_created' ||
          eventData.action === 'refresh_grids') {
        logger.log('🔄 [Confirmations] Triggering grid refresh');
        triggerGridRefresh();
      }
    }
  });

  // Load confirmation when trade selected
  const loadConfirmation = useCallback(
    async (tradeId: string, tradeOrgId: string) => {
      if (!clientId) return;

      setLoading(true);
      setError(null);
      try {
        // Use the trade's own organizationId (supports multi-entity disambiguation)
        const data = await clientService.getTradeConfirmation(tradeOrgId, tradeId);
        setConfirmationData(data);
        // Default to email tab, switch to PDF if no email body but PDF exists
        if (!data?.body && data?.hasPdf) {
          setActiveTab('pdf');
        } else {
          setActiveTab('email');
        }
      } catch (err) {
        logger.error('Failed to load confirmation:', err);
        setError(err instanceof Error ? err.message : 'Failed to load confirmation');
        setConfirmationData(null);
      } finally {
        setLoading(false);
      }
    },
    [clientId]
  );

  useEffect(() => {
    // Check if trade has a confirmation (status = matched)
    const tradeStatus = selectedTrade?.status || selectedTrade?.Status;
    if (tradeStatus === 'matched' && clientId && selectedTrade) {
      const tradeId =
        (selectedTrade?.FirestoreDocId as string) ||
        (selectedTrade?.firestoreDocId as string) ||
        (selectedTrade?.id as string);
      const tradeOrgId = selectedTrade?.organizationId as string;
      if (tradeId && tradeOrgId) {
        loadConfirmation(tradeId, tradeOrgId);
      }
    } else {
      setConfirmationData(null);
      setError(null);
    }
  }, [selectedTrade, clientId, loadConfirmation]);

  const handleTradeSelected = (trade: Record<string, unknown> | null) => {
    setSelectedTrade(trade);
  };

  // Load exceptions count on mount and after grid refresh
  const loadExceptionsCount = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await clientService.getConfirmationExceptions(clientId);
      if (result.success && result.data) {
        setExceptionsCount(result.data.recentCount ?? result.data.totalCount ?? 0);
      }
    } catch (err) {
      logger.error('Failed to load exceptions count:', err);
    }
  }, [clientId]);

  useEffect(() => {
    loadExceptionsCount();
  }, [loadExceptionsCount, refreshTrigger]);

  return (
    <div className="confirmations-page">
      {/* Header */}
      <div className="confirmations-header">
        <div className="confirmations-title">{t('confirmations.title')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {dateFrom !== null && (
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo || ''}
              onApply={(from, to) => { setDateFrom(from); setDateTo(to); }}
            />
          )}
          <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
          <button
            className="exceptions-bell-btn"
            onClick={() => setShowExceptionsModal(true)}
            title={t('confirmations.exceptions.title')}
          >
            <Bell size={20} />
            {exceptionsCount > 0 && (
              <span className="exceptions-badge">{exceptionsCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Resizable Panel Layout */}
      <PanelGroup
        orientation="vertical"
        className="confirmations-panel-group"
      >
        {/* Top Section */}
        <Panel defaultSize={65} minSize={30}>
          <PanelGroup
            orientation="horizontal"
            className="confirmations-top-panels"
          >
            {/* Left Panel - Preview */}
            <Panel defaultSize={75} minSize={30}>
              <div className="preview-panel">
                <div className="preview-tabs">
                  <button
                    className={`tab ${activeTab === 'email' ? 'active' : ''}`}
                    onClick={() => setActiveTab('email')}
                  >
                    {confirmationData?.processingSource === 'counterparty_api'
                      ? t('confirmations.apiPayload')
                      : t('confirmations.emailBody')}
                  </button>
                  {confirmationData?.hasPdf && (
                    <button
                      className={`tab ${activeTab === 'pdf' ? 'active' : ''}`}
                      onClick={() => setActiveTab('pdf')}
                    >
                      PDF
                    </button>
                  )}
                </div>
                <div className="preview-content">
                  {activeTab === 'email' ? (
                    <EmailPreviewPanel
                      confirmationData={confirmationData}
                      loading={loading}
                      error={error}
                      selectedField={selectedField}
                      highlightText={highlightText}
                      occurrenceIndex={highlightOccurrenceIndex}
                      comparisonResult={selectedFieldResult}
                    />
                  ) : (
                    <PdfPreviewPanel
                      pdfUrl={confirmationData?.pdfUrl}
                      loading={loading}
                      selectedField={selectedField}
                      fieldCoordinates={confirmationData?.fieldCoordinates}
                      comparisonResult={selectedFieldResult}
                    />
                  )}
                </div>
              </div>
            </Panel>

            <ResizeHandle direction="horizontal" />

            {/* Right Panel - Comparison */}
            <Panel defaultSize={25} minSize={15}>
              <div className="comparison-panel">
                <ComparisonTable
                  trade={selectedTrade}
                  confirmationData={confirmationData}
                  loading={loading}
                  onActionSuccess={(msg) => showToastNotification(msg, 'success')}
                  onActionError={(msg) => showToastNotification(msg, 'error')}
                  onGridRefreshNeeded={triggerGridRefresh}
                  selectedField={selectedField}
                  onFieldSelected={setSelectedField}
                  readOnly={(selectedTrade?.workflowState || selectedTrade?.WorkflowState) === 'PendingModification'}
                />
              </div>
            </Panel>
          </PanelGroup>
        </Panel>

        <ResizeHandle direction="vertical" />

        {/* Bottom Panel - Grid */}
        <Panel defaultSize={35} minSize={15}>
          <div className="bottom-panel expanded">
            <div className="panel-content">
              {dateFrom === null ? null : (
                <ClientTradesGrid
                  onRowSelected={handleTradeSelected}
                  menuContext="matching"
                  refreshTrigger={refreshTrigger}
                  hideUploadControls={true}
                  hideOverwriteCheckbox={true}
                  compactControls={true}
                  workflowStates={['Booked', 'Confirmed', 'Cancelled', 'PendingModification'] as WorkflowState[]}
                  dateFrom={dateFrom || undefined}
                  dateTo={dateTo || undefined}
                  exportTrigger={exportTrigger}
                  onExportData={handleExportData}
                  clearFiltersTrigger={clearFiltersTrigger}
                />
              )}
            </div>
            {/* Export + Refresh buttons — below the grid, bottom-right */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              padding: '4px 8px 0',
            }}>
              <button
                onClick={() => setClearFiltersTrigger(prev => prev + 1)}
                className="btn-secondary btn-small"
                style={{ fontSize: '0.85rem' }}
              >
                {t('common.clearFilters', 'Clear Filters')}
              </button>
              <button
                onClick={() => setExportTrigger(prev => prev + 1)}
                className="btn-secondary btn-small"
                style={{ fontSize: '0.85rem' }}
              >
                {t('common.exportExcel', 'Export Excel')}
              </button>
              <button
                onClick={triggerGridRefresh}
                className="btn-secondary btn-small"
                style={{ fontSize: '0.85rem' }}
              >
                {t('common.refresh', 'Refresh')}
              </button>
            </div>
          </div>
        </Panel>
      </PanelGroup>

      {/* Real-time Toast Notifications */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={5000}
      />

      {/* Exceptions Modal */}
      {clientId && (
        <ExceptionsModal
          clientId={clientId}
          isOpen={showExceptionsModal}
          onClose={() => {
            setShowExceptionsModal(false);
            loadExceptionsCount(); // Refresh count when modal closes
          }}
          onRefreshNeeded={() => {
            triggerGridRefresh();
            loadExceptionsCount();
          }}
          defaultDateFrom={dateFrom || undefined}
          defaultDateTo={dateTo || undefined}
        />
      )}
    </div>
  );
};

export default Confirmations;
